import type { KeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { CornerDownLeft, Search } from 'lucide-react';
import { Dialog, cn } from '@eombor/ui';
import { t } from '../../shared/i18n/index.js';

export type CommandGroup = 'pages' | 'actions';

export interface CommandItem {
  id: string;
  label: string;
  /** O'ng chetdagi qisqa izoh — nima bo'lishini oldindan aytadi. */
  hint?: string;
  icon?: ReactNode;
  group?: CommandGroup;
  /** Qidiruvga qo'shimcha so'zlar (manzil, sinonim). */
  keywords?: string;
  run: () => void;
}

export interface CommandPaletteProps {
  items: readonly CommandItem[];
  /** Berilsa — ochilish tashqaridan boshqariladi (topbar tugmasi uchun). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const GROUP_LABEL: Record<CommandGroup, string> = {
  pages: 'palette.groupPages',
  actions: 'palette.groupActions',
};

const GROUP_ORDER: readonly CommandGroup[] = ['pages', 'actions'];

const normalize = (value: string): string => value.trim().toLowerCase();

const matches = (item: CommandItem, query: string): boolean => {
  if (query.length === 0) return true;
  const haystack = normalize(`${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`);
  return haystack.includes(query);
};

/** Klaviatura yorlig'i chipi — `⌘K` yoki `Ctrl K`. */
export const Kbd = ({ children }: { children: ReactNode }) => (
  <kbd
    className={cn(
      'tabular inline-flex h-5 min-w-5 items-center justify-center px-1',
      'rounded-[var(--radius-sm)] border border-border bg-surface-2',
      'text-xs leading-none font-medium text-text-faint',
    )}
  >
    {children}
  </kbd>
);

/**
 * Buyruq paneli — skaner bilan ishlaydigan xodim uchun **asosiy** navigatsiya
 * yo'li, qo'shimcha bezak emas: `⌘K` / `Ctrl+K` ochadi, yozish filtrlaydi,
 * o'q tugmalari tanlaydi, `Enter` bajaradi, `Esc` yopadi.
 *
 * Modal mantiqning o'zi `Dialog` primitivida turadi (fokus qopqoni, `Esc`,
 * fokusni qaytarish) — bu yerda ikkinchi modal amalga oshiruvi yozilmagan.
 */
export const CommandPalette = ({ items, open, onOpenChange }: CommandPaletteProps) => {
  const listId = useId();
  const optionId = (index: number): string => `${listId}-option-${String(index)}`;

  const controlled = open !== undefined;
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlled ? open : internalOpen;

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  /* Ro'yxat oxiriga yetmaganda pastda nozik chiziq turadi — "davomi bor"
     degan yagona signal. */
  const [scrolled, setScrolled] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  const setOpen = useCallback(
    (next: boolean): void => {
      if (!controlled) setInternalOpen(next);
      onOpenChange?.(next);
    },
    [controlled, onOpenChange],
  );

  /* `⌘K` / `Ctrl+K` butun hujjatda tinglanadi — fokus qayerda bo'lishidan
     qat'i nazar ishlaydi. Brauzerning o'z yorlig'i bosilmasin deb `preventDefault`. */
  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent): void => {
      if (event.key.toLowerCase() !== 'k') return;
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      setOpen(!isOpen);
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, setOpen]);

  /* Har ochilishda toza varaqdan boshlanadi va fokus darhol maydonda bo'ladi —
     odam ochib, to'g'ridan-to'g'ri yozishga tushadi. */
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setSelected(0);
    inputRef.current?.focus();
  }, [isOpen]);

  const normalizedQuery = normalize(query);

  /* Guruhlar tartibi qat'iy, lekin tanlov ro'yxati **tekis**: o'q tugmasi
     guruh chegarasida to'xtab qolmaydi. */
  const groups = useMemo(() => {
    const found = items.filter((item) => matches(item, normalizedQuery));
    return GROUP_ORDER.map((group) => ({
      group,
      items: found.filter((item) => (item.group ?? 'pages') === group),
    })).filter((entry) => entry.items.length > 0);
  }, [items, normalizedQuery]);

  const flat = useMemo(() => groups.flatMap((entry) => entry.items), [groups]);

  /* Ro'yxat qisqarganda tanlov chegaradan chiqib ketmasin. */
  useEffect(() => {
    setSelected((current) => (current >= flat.length ? 0 : current));
  }, [flat.length]);

  /* Tanlangan qator doim ko'rinib tursin (uzun ro'yxatda o'q bilan yurganda).
     `id` bo'yicha emas, `aria-selected` bo'yicha qidiramiz — `useId` qaytaradigan
     belgilar `querySelector` uchun qochirishni talab qilmasin. */
  useEffect(() => {
    listRef.current
      ?.querySelector('[aria-selected="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected, flat.length]);

  const runAt = (index: number): void => {
    const item = flat[index];
    if (item === undefined) return;
    setOpen(false);
    item.run();
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((current) => (flat.length === 0 ? 0 : (current + 1) % flat.length));
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((current) => (flat.length === 0 ? 0 : (current - 1 + flat.length) % flat.length));
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      setSelected(0);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      setSelected(Math.max(flat.length - 1, 0));
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      runAt(selected);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => setOpen(false)}
      title={t('palette.title')}
      // `cn` sinflarni faqat birlashtiradi (tailwind-merge emas), shuning uchun
      // `Dialog` ning o'z `max-w-*` va `max-h-*` sinflarini `!` bilan
      // ustidan yozamiz.
      //
      // Balandlik `dvh` da: telefon brauzerining pastki paneli chiqib-kirib
      // turadi va `vh` o'sha panel ostida qolgan qismni hisobga olmaydi —
      // ro'yxatning oxiri ekrandan chiqib ketardi.
      className="max-h-[85dvh]! max-w-[34rem]! sm:max-h-[min(80dvh,40rem)]!"
      /* Tana AYLANMAYDI — aylantiruvchi ro'yxatning o'zida. Ikkalasi ham
         aylanganda ro'yxat balandligi oynanikidan mustaqil bo'lib qolardi:
         aynan shundan yarim kesilgan qator va uning ostidagi bo'sh joy
         paydo bo'lgan edi. */
      bodyClassName="flex min-h-0 flex-col overflow-hidden"
      footer={
        /* Klaviatura maslahatlari FAQAT klaviaturasi bor qurilmada:
           teginish ekranida «Esc» va o'q tugmalari bajarib bo'lmaydigan
           ko'rsatma bo'lib, pastki qatorni bekorga band qilardi. */
        <div className="hidden w-full items-center gap-4 text-xs text-text-faint [@media_(hover:hover)_and_(pointer:fine)]:flex">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            {t('palette.hintMove')}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>
              <CornerDownLeft size={11} aria-hidden />
            </Kbd>
            {t('palette.hintSelect')}
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>Esc</Kbd>
            {t('palette.hintClose')}
          </span>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3">
        {/* Qidiruv maydoni QOTIB turadi: u ro'yxat bilan birga aylansa,
            uzun ro'yxatda yozgan matnini ko'rish uchun tepaga qaytish
            kerak bo'lardi. */}
        <div className="relative flex shrink-0 items-center">
          <Search
            size={16}
            aria-hidden
            className="pointer-events-none absolute left-3 text-text-faint"
          />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-autocomplete="list"
            aria-label={t('palette.placeholder')}
            aria-activedescendant={flat.length > 0 ? optionId(selected) : undefined}
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelected(0);
            }}
            onKeyDown={onInputKeyDown}
            className={cn(
              'h-[var(--size-control)] w-full rounded-[var(--radius-sm)]',
              'border border-border-control bg-surface pr-3 pl-9',
              'text-base text-text placeholder:text-text-faint',
              'transition-[border-color] duration-[var(--motion-fast)]',
              'ease-[var(--ease-standard)] hover:border-border-strong',
            )}
          />
        </div>

        {flat.length === 0 ? (
          <p className="px-1 py-10 text-center text-sm text-text-muted">
            {/* So'rovning O'ZI javobda takrorlanadi: "hech narsa topilmadi"
                yolg'iz turganda terish xatosi ko'zga tashlanmasdi. */}
            {normalizedQuery.length === 0 ? t('palette.empty') : t('palette.emptyQuery', { query })}{' '}
            <span className="text-text-faint">{t('palette.emptyHint')}</span>
          </p>
        ) : (
          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={t('palette.results')}
            onScroll={(event) => {
              const el = event.currentTarget;
              setScrolled(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
            }}
            className={cn(
              // Balandlik QAT'IY emas: ro'yxat oynada qolgan butun joyni
              // egallaydi va oyna ostida bo'sh maydon qolmaydi.
              '-mx-1 min-h-0 flex-1 overflow-y-auto px-1 pb-2',
              // Modal ichidagi siljish orqadagi sahifani qimirlatmaydi.
              'overscroll-contain [scrollbar-width:thin]',
              // Davomi borligini GRADIENT emas, nozik chiziq aytadi: fade
              // ostidagi matnni o'qib bo'lmasdi va u yarim qatorni
              // "ataylab shunday" qilib ko'rsatardi.
              scrolled ? 'border-b border-border' : 'border-b border-transparent',
            )}
          >
            {groups.map((entry) => (
              <li key={entry.group} role="presentation">
                {/* Guruh nomi `aria-label` da ham bor — ekran o'quvchi ikki marta o'qimasin. */}
                <p
                  aria-hidden
                  className={cn(
                    'px-2 pt-3 pb-1 text-xs font-medium tracking-wide uppercase',
                    'text-text-faint',
                    // Aylantirilganda bo'lim nomi tepada qoladi: uzun
                    // ro'yxatda "bu qaysi guruh?" degan savol tug'ilmasin.
                    // Fon SHAFFOF EMAS — ostidan qator matni ko'rinib
                    // turardi.
                    'sticky top-0 z-10 bg-surface',
                  )}
                >
                  {t(GROUP_LABEL[entry.group])}
                </p>
                <ul
                  role="group"
                  aria-label={t(GROUP_LABEL[entry.group])}
                  className="flex flex-col gap-0.5"
                >
                  {entry.items.map((item) => {
                    const index = flat.indexOf(item);
                    const active = index === selected;

                    return (
                      <li
                        key={item.id}
                        id={optionId(index)}
                        role="option"
                        aria-selected={active}
                        onMouseMove={() => setSelected(index)}
                        onClick={() => runAt(index)}
                        className={cn(
                          'flex h-10 cursor-pointer items-center gap-2.5 rounded-[var(--radius-sm)] px-2',
                          'transition-[background-color,color] duration-[var(--motion-fast)]',
                          'ease-[var(--ease-standard)]',
                          active ? 'bg-accent-soft text-text' : 'text-text-muted',
                        )}
                      >
                        <span
                          aria-hidden
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center',
                            active ? 'text-accent' : 'text-text-faint',
                          )}
                        >
                          {item.icon}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-sm font-medium">
                          {item.label}
                        </span>
                        {item.hint === undefined ? null : (
                          <span className="tabular shrink-0 text-xs text-text-faint">
                            {item.hint}
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Dialog>
  );
};
